import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

type PillSize = 'medium' | 'small' | 'xsmall';

type PillVariant = 'default' | 'accentBrand';

interface PillProps {
  label: string;
  /** Fully rounded corners. */
  rounded?: boolean;
  size?: PillSize;
  variant?: PillVariant;
  /** When true, renders `small` below the MD breakpoint and `medium` at MD+. */
  responsive?: boolean;
  className?: string;
}

// bricks Pill heights/paddings are intrinsic (text + padding) since bricks'
// fixed 33px height has no clean Tailwind token. `xsmall` shares `small`'s
// padding; bricks renders it at the same scale with auto height.
const SIZE_STYLES: Record<PillSize, string> = {
  medium: 'px-3 py-1',
  small: 'px-2 py-0.5',
  xsmall: 'px-2 py-0.5',
};

const SIZE_TEXT: Record<PillSize, string> = {
  medium: 'text-body-md',
  small: 'text-body-sm',
  xsmall: 'text-body-sm',
};

// default: white background, primary border, tertiary label (bricks `default`).
// accentBrand: bricks resolves to blue-100 surface + blue-800 label.
const VARIANT_STYLES: Record<PillVariant, string> = {
  default: 'border-od-border-primary bg-od-bg-primary',
  accentBrand: 'border-blue-100 bg-blue-100',
};

const VARIANT_TEXT: Record<PillVariant, string> = {
  default: 'text-od-content-tertiary',
  accentBrand: 'text-od-content-hover-accent-brand',
};

const RESPONSIVE_STYLES = 'px-2 py-0.5 md:px-3 md:py-1';
const RESPONSIVE_TEXT = 'text-body-sm md:text-body-md';

/** Status pill built on atoms primitives. Replaces the bricks-next Pill. */
export const Pill: React.FC<PillProps> = ({
  label,
  rounded,
  size = 'medium',
  variant = 'default',
  responsive,
  className,
}) => {
  return (
    <Box
      className={cn(
        'w-max items-center justify-center border',
        VARIANT_STYLES[variant],
        rounded ? 'rounded-full' : 'rounded',
        responsive ? RESPONSIVE_STYLES : SIZE_STYLES[size],
        className,
      )}
    >
      <Text
        as="span"
        className={cn(responsive ? RESPONSIVE_TEXT : SIZE_TEXT[size], VARIANT_TEXT[variant])}
      >
        {label}
      </Text>
    </Box>
  );
};
